/**
 * `TurnActivity` — the concierge's work for ONE turn, printed to the
 * conversation the way a terminal prints to a window.
 *
 * ## Why this is a flat log and NOT a collapsible disclosure
 *
 * It was a disclosure once (a "Trabalhou por X ›" header hiding the steps), and
 * the operator retired that on 2026-08-13 after watching it on real turns:
 *
 *   *"the request was much simpler: to show them sequentially on the conversation
 *   history, similarly to what claude code does, simple output to a terminal
 *   window. […] this may not be appropriate for long tasks, they just have to
 *   keep outputing progress to the window so the user can scroll back and revisit
 *   everything."*
 *
 * The disclosure did two jobs badly. It **hid history exactly when the turn was
 * long** — the case where history is worth having — and it nested a scrolling
 * box inside a scrolling transcript, so two scrollbars competed. A mutating
 * status header made it worse: once every step prints its own row, a header
 * saying "Lendo FOLDERS.md…" is just a second copy of the last row, which is
 * why the surface still read as "one line changing" even after steps appended.
 *
 * So: steps stream in, permanently, oldest-first. Nothing collapses. The total
 * duration prints once, immediately above the answer, as the last thing the
 * work says before the concierge speaks.
 *
 * ⚠ Do not re-add a collapse, a max-height, or an inner scroller here. The
 * transcript is the scroll surface; this is content inside it.
 *
 * ## The one live cue that stays
 *
 * A bare "Pensando…" line at the BOTTOM of the steps while the turn works.
 * Measured on real turns, the first tool does not fire for 14-20 s — without
 * this the window is simply silent for that stretch, which is the gap L0 was
 * built to fill. It sits where a terminal's spinner sits and vanishes when the
 * answer starts.
 *
 * ## ⚠ Scroll contract — unchanged and still binding
 *
 * This must render inside `ChatMessageList`'s root element, which is the
 * `firstElementChild` `ConversationShell`'s `useStickToBottom` ResizeObserves —
 * so rows appearing mid-stream are re-pinned by a mechanism that already
 * honours "the reader scrolled up". Nothing here calls `scrollIntoView`, and
 * nothing here belongs in `ChatMessageList`'s force-scroll dep list.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LexiunSpinner } from "@/components/home/LexiunSpinner";
import { BlockRenderer } from "@/components/chat/BlockRenderer";
import {
  GENERIC_TOOL_LABEL_KEY,
  SYSTEM_STEP_NAME,
  activityTarget,
  formatWorked,
  isChecklistTool,
  providerDisplayLabel,
  showsTarget,
} from "@/lib/chat/tool-activity";
import type { ContentBlock } from "@/types/content-blocks";
import type { ToolTrailEntry } from "@/types/gateway";
import { cn } from "@/lib/utils";

interface TurnActivityProps {
  /** The tool steps of this turn, in order (the LIVE path — arg-carrying
   *  `tool_use` blocks the rich renderers can dispatch on). */
  blocks: ContentBlock[];
  /**
   * The PERSISTED trail of a reloaded trail-bearing (`v >= 2`) turn. When
   * present it replaces `blocks` as the step source. v:3 entries may carry
   * `provider`/`path` (A-11) — the badge renders iff `provider` is present.
   *
   * ⚠ These are NOT blocks and must never be turned into blocks: an entry
   * carries `{name, label?, target?, outcome}` and no args, deliberately (D5),
   * so `ReadBadge` and friends would render their "unparseable input" cards.
   */
  trail?: ToolTrailEntry[];
  /** Frozen work duration. ⚠ `undefined` = never observed ⇒ NO duration is
   *  rendered. Never zero, never a substitute — a run-end figure would include
   *  the answer-streaming tail and render larger than what the lawyer watched
   *  freeze. */
  workedMs?: number;
  /** `true` while the turn is in flight — drives the trailing "Pensando…" cue. */
  live?: boolean;
  /** Epoch ms the run began. Drives the ticking clock beside "Pensando…" so the
   *  lawyer can see time passing rather than only that something is happening —
   *  a still spinner and a wedged one look identical. */
  startedAt?: number | null;
  /** The run has been event-silent past RUN_STALL_REVEAL_MS. Renders a QUIET
   *  inline affordance under the working cue (operator directive 2026-08-14 —
   *  it was a full-width amber banner over the composer, the loudest thing on
   *  the page while long turns worked fine). Reveal-only; never automatic. */
  stalled?: boolean;
  /** The LOCAL release — clears this browser's run state, claims nothing about
   *  the server. Post-daadae722 the hint's promise ("the answer will still
   *  appear") is literally true. */
  onRelease?: () => void;
  /** Measured per-step outcomes keyed by block id (`toolCallId`). Absent = the
   *  step has not reported yet; there is no inferred success anywhere. */
  outcomes?: Record<string, "ok" | "error">;
  /** ⚖ A-11 — per-step provenance for a LIVE-finalized turn, keyed by block
   *  id, merged at turn end by the hydration pass. The reload path never uses
   *  this: persisted entries carry `provider` directly. Absent = no badge. */
  attribution?: Record<string, { provider: string; path?: string }>;
  runId?: string;
}

export function TurnActivity({
  blocks,
  trail,
  workedMs,
  live = false,
  startedAt,
  stalled,
  onRelease,
  outcomes,
  attribution,
  runId,
}: TurnActivityProps) {
  const { t } = useTranslation();

  // A reloaded turn renders its persisted trail; a live turn renders blocks.
  // Exactly one source is ever in play.
  const stepCount = trail ? trail.length : blocks.length;
  const frozen = typeof workedMs === "number";

  const worked = frozen ? formatWorked(workedMs) : null;
  const durationText = worked
    ? worked.min > 0
      ? worked.sec > 0
        ? t("chat.activity.workedMinSec", { min: worked.min, sec: worked.sec })
        : t("chat.activity.workedMin", { count: worked.min })
      : t("chat.activity.workedSec", { count: worked.sec })
    : null;

  // A live turn always says something, even before its first step — that
  // silence is the thing this feature exists to remove.
  const showWorkingCue = live && !frozen;

  // The clock beside "Pensando…", ticking once a second (operator, 2026-08-13).
  // A spinner alone proves only that the UI is animating; a rising number is the
  // difference between "this is taking a while" and "is this stuck?" — the same
  // question the stall-release affordance exists to answer, asked earlier and
  // more cheaply.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!showWorkingCue || startedAt == null) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [showWorkingCue, startedAt]);

  // The rotating verb, revived from the retired `ThinkingCue` at the operator's
  // request (2026-08-13). It went away with the fixed composer cue; the ask is
  // for it back HERE, beside the clock.
  //
  // ⚠ Localized this time. The original list was hardcoded English on a fully
  // pt-BR surface — tolerable when it was one word above the composer, wrong now
  // that it sits inside the transcript next to localized activity rows.
  const verbs = t("chat.activity.verbs", { returnObjects: true }) as unknown;
  const verbList = Array.isArray(verbs) && verbs.length > 0 ? (verbs as string[]) : null;
  const [verbIdx, setVerbIdx] = useState(0);
  useEffect(() => {
    if (!showWorkingCue || !verbList) return;
    const id = setInterval(() => {
      // Random, but never twice in a row — a verb that "changes" to itself reads
      // as a frozen UI, which is the opposite of what this cue is for.
      setVerbIdx((prev) => {
        if (verbList.length === 1) return 0;
        let next = prev;
        while (next === prev) next = Math.floor(Math.random() * verbList.length);
        return next;
      });
    }, 2_400);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWorkingCue, verbList?.length]);

  const verbText = verbList ? `${verbList[verbIdx % verbList.length]}…` : t("chat.activity.thinking");

  const elapsed =
    showWorkingCue && startedAt != null ? formatWorked(Math.max(0, nowMs - startedAt)) : null;
  // Compact units, matching the frozen line: "1m 44s", never "1 min 44 s".
  const elapsedText = elapsed
    ? elapsed.min > 0
      ? `${elapsed.min}m ${elapsed.sec}s`
      : `${elapsed.sec}s`
    : null;

  if (stepCount === 0 && !durationText && !showWorkingCue) return null;

  return (
    <div
      data-testid="turn-activity"
      data-live={live || undefined}
      data-steps={stepCount}
      // `gap-3` rather than the old `gap-1.5`: these are separate events in a
      // log, not rows of one table, and the operator asked for air between the
      // tasks card and its neighbours specifically.
      className="flex min-w-0 flex-col gap-3"
    >
      {trail
        ? trail.map((entry, i) => <TrailRow key={i} entry={entry} />)
        : blocks.map((block, i) => {
            const id = block.type === "tool_use" ? block.id : undefined;
            const outcome = id ? outcomes?.[id] : undefined;
            // ── THE CHECKLIST UNIFICATION (operator, 2026-08-14) ────────
            // Every ONE-LINE chip-class step renders as the flat trail row
            // LIVE too — the same `TrailRow` the reload path uses, so the two
            // views stop differing by accident. The chips-vs-checklist split
            // was an implementation ARTIFACT (live steps had args, so they
            // grew arg-driven chip renderers; persisted entries carry no args
            // by design, so reload grew the flat list) — not a design. Args
            // SURVIVE as the target text via `activityTarget`; only the box
            // styling goes. Rich renderers (TodoCard, EditBlock, WriteBlock)
            // are NOT boxes around a line and stay rich — that boundary is
            // with the operator for a quick ruling.
            if (block.type === "tool_use" && isChecklistTool(block.name)) {
              const target =
                block.name === SYSTEM_STEP_NAME
                  ? undefined
                  : activityTarget(block.name, (block.input ?? {}) as Record<string, unknown>);
              // ⚖ A-11 — hydrated provenance folds into the synthesized entry,
              // so the badge renders through the SAME TrailRow path a reloaded
              // entry uses. Absent = no badge, no placeholder.
              const attrib = id ? attribution?.[id] : undefined;
              return (
                <TrailRow
                  key={id ?? i}
                  entry={{
                    name: block.name,
                    ...(target ? { target } : {}),
                    outcome: outcome ?? "unknown",
                    ...(attrib
                      ? {
                          provider: attrib.provider,
                          ...(attrib.path ? { path: attrib.path } : {}),
                        }
                      : {}),
                  }}
                />
              );
            }
            return (
              <div
                key={id ?? i}
                data-testid="turn-activity-step"
                data-outcome={outcome}
                className={cn("min-w-0", outcome === "error" && "opacity-90")}
              >
                <BlockRenderer block={block} runId={runId} />
              </div>
            );
          })}

      {showWorkingCue && stalled && onRelease && (
        <div
          data-testid="turn-activity-stalled"
          className="flex flex-wrap items-baseline gap-x-2 text-caption text-cream-500 dark:text-cream-500"
        >
          <span>{t("caseWorkspace.upload.stalledNotice")}</span>
          <button
            type="button"
            data-testid="turn-activity-release"
            onClick={onRelease}
            title={t("caseWorkspace.upload.stalledHint")}
            className="font-medium text-coffee-600 underline decoration-cream-400 underline-offset-2 hover:text-coffee-800 dark:text-cream-400 dark:hover:text-cream-200"
          >
            {t("caseWorkspace.upload.stalledRelease")}
          </button>
        </div>
      )}

      {showWorkingCue && (
        <div
          data-testid="turn-activity-working"
          role="status"
          className="flex select-none items-center gap-1.5 text-xs font-medium text-coffee-700 dark:text-cream-300"
        >
          <LexiunSpinner
            variant="rotate"
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 text-coffee-900 dark:text-gold-400"
          />
          <span className="animate-text-shimmer">{verbText}</span>
          {elapsedText && (
            <span data-testid="turn-activity-elapsed" className="tabular-nums text-cream-500">
              {elapsedText}
            </span>
          )}
        </div>
      )}

      {/* The duration is the LAST thing the work says before the answer — the
          operator's "show the total duration at the beginning of the actual
          model response". It is a plain line, not a control: there is nothing
          left to expand. */}
      {durationText && (
        <div
          data-testid="turn-activity-duration"
          // Deliberately recessive (operator, 2026-08-13): light weight, muted
          // colour. It is a footnote on the work, not a heading for the answer
          // that follows it — the answer is what the lawyer came for.
          className="select-none text-caption font-light tabular-nums text-cream-500 dark:text-cream-500"
        >
          {durationText}
        </div>
      )}
    </div>
  );
}

/**
 * One step of a RELOADED turn's persisted trail.
 *
 * Purpose-built rather than reusing the `ToolUseBlock` dispatcher, because a
 * persisted entry has no args to dispatch on (D5). It renders exactly what was
 * stored: the friendly label when the writer supplied one, a short localized
 * noun when it did not, the target, and the measured outcome.
 *
 * ⚠ `unknown` renders NEUTRAL — never a success mark. An unobserved result is
 * not a successful one, which is the whole reason `outcome` is required with
 * `"unknown"` as a real member.
 */
function TrailRow({ entry }: { entry: ToolTrailEntry }) {
  const { t } = useTranslation();
  const mark = entry.outcome === "ok" ? "✓" : entry.outcome === "error" ? "✕" : "·";
  // A redacted System-tier step (DS-37): the target was dropped at ingest, so
  // there is nothing here to leak — this branch only chooses the wording.
  const isSystem = entry.name === SYSTEM_STEP_NAME;
  return (
    <div
      data-testid="turn-activity-step"
      data-trail-row="true"
      data-outcome={entry.outcome}
      className="flex min-w-0 items-baseline gap-1.5 text-caption text-coffee-600 dark:text-cream-400"
    >
      <span
        aria-label={t(`chat.activity.outcome.${entry.outcome}`)}
        title={t(`chat.activity.outcome.${entry.outcome}`)}
        className={cn(
          "shrink-0 tabular-nums",
          entry.outcome === "ok" && "text-gold-600 dark:text-gold-400",
          entry.outcome === "error" && "text-red-600 dark:text-red-400",
          entry.outcome === "unknown" && "text-cream-500 dark:text-cream-500",
        )}
      >
        {mark}
      </span>
      {/* `label ?? localized noun ?? name` — a writer-supplied label wins; when
          absent (measured: always) a short noun is used; an unmapped tool keeps
          its raw name rather than acquiring an invented word.

          ⚠ Deliberately NOT the D1 live verb phrases: those carry the target
          ("Lendo {{target}}…") and this row renders `target` separately, so
          reusing them would print it twice and call a finished step ongoing. */}
      <span className="shrink-0 font-medium">
        {isSystem
          ? t("chat.activity.systemStep")
          : // ⚠ The last resort is the GENERIC, never `entry.name` (operator
            // directive 2026-08-14 — identifiers are internals, DS-37's sibling
            // doctrine). A writer-supplied label still wins; a mapped tool shows
            // its noun; an unmapped one says "Ferramenta interna".
            (entry.label ??
              t(`chat.activity.trailLabel.${entry.name}`, {
                defaultValue: t(GENERIC_TOOL_LABEL_KEY),
              }))}
      </span>
      {entry.target && showsTarget(entry.name) && (
        <span className="min-w-0 truncate text-cream-500 dark:text-cream-500" title={entry.target}>
          {entry.target}
        </span>
      )}
      {/* ⚖ A-11 — the provider badge, at the end of the row (operator: "a
          badge or similar at the end of the search"). Recessive, a footnote on
          the row like the duration line is on the turn.

          Honesty rules, all load-bearing:
          - Renders ONLY when the writer recorded a provider — absence renders
            NOTHING (no placeholder, no guess). Under v>=3 absence means "the
            result named none"; under v:2 "the writer could not know"; both
            correctly show no badge.
          - `"http"` is the SENTINEL for "no provider involved — fetched
            directly" and renders the localized direct-access wording, never
            the raw string and never as a ladder member.
          - Provider ids are an OPEN vocabulary: a known id gets its display
            label, an UNKNOWN id renders VERBATIM via defaultValue — never a
            generic word (A-11's directive is that identity be visible; a
            plausible-but-wrong name would be an attribution lie, and a
            generic one defeats the directive). */}
      {entry.provider && (
        <span
          data-testid="trail-provider-badge"
          data-provider={entry.provider}
          className="shrink-0 select-none text-cream-500 dark:text-cream-500"
        >
          {entry.provider === "http"
            ? t("chat.activity.provider.direct")
            : t("chat.activity.provider.via", {
                provider: providerDisplayLabel(t, entry.provider),
              })}
        </span>
      )}
    </div>
  );
}
